import {
  AlignmentType,
  convertInchesToTwip,
  Document,
  Footer,
  ImageRun,
  NumberFormat,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';

import { QUESTION_TYPES } from '@api/instant_survey';

import {
  getMaxDiffQuestionInfo,
  getNumericTypeText,
  getQuestionCost,
  getQuestionDisplayIndexById,
} from '../../../../../utilities/helpers';
import {
  A_CHAR_CODE,
  BASE_TEXT_CONFIG,
  BLOCK_SPACING,
  BOLD_TEXT_CONFIG,
  BORDERLESS_TABLE_CONFIG,
  BULLET_SYMBOL,
  BULLETED_LIST_CONFIG,
  CHECKBOX_SYMBOL,
  DASH_SYMBOL,
  DEFAULT_SPACING,
  EM_SPACE,
  FILLED_CIRCLE_SYMBOL,
  HEADER_SPACING,
  INDENT_LG,
  INDENT_MD,
  INDENT_SM,
  MAX_IMAGE_WIDTH,
  NUMERATOR_FULL_LOGO_URL,
  OPTION_TEXT_CONFIG,
  QUESTION_SPACING,
  RADIO_SYMBOL,
  SMALL_TEXT_SIZE,
} from './constants';

const getImageDimensions = async (imageData, imageType) => {
  const blob = new Blob([imageData], { type: `image/${imageType}` });
  const url = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ height: img.naturalHeight, width: img.naturalWidth });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};

const normalizeImageType = (contentType) => {
  if (!contentType) return null;

  const match = contentType.toLowerCase().match(/image\/(jpeg|jpg|png|gif|bmp)/);
  return match ? match[1] : null;
};

const prefetchImages = async (items) => {
  const filteredItems = (items || []).filter((item) => item.image?.cdnUrl);
  // Batch size of 6 respects HTTP/1.1 browser limit of ~6 concurrent connections per host
  const BATCH_SIZE = 6;
  const allResults = [];

  for (let i = 0; i < filteredItems.length; i += BATCH_SIZE) {
    const batch = filteredItems.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.all(
      batch.map(async (item) => {
        try {
          const response = await fetch(item.image.cdnUrl);
          if (!response.ok) return null;

          const contentType = response.headers.get('content-type');
          const imageType = normalizeImageType(contentType);

          // Skip if not a supported image type (docx library only supports: jpeg, jpg, bmp, gif, png)
          if (!imageType) {
            console.warn(
              'Unsupported image type, skipping:',
              contentType || 'unknown',
              item.image.cdnUrl
            );
            return null;
          }

          const arrayBuffer = await response.arrayBuffer();
          if (arrayBuffer.byteLength === 0) return null;

          try {
            const dimensions = await getImageDimensions(arrayBuffer, imageType);
            if (dimensions.width <= 0 || dimensions.height <= 0) {
              console.warn('Invalid image dimensions, skipping image:', dimensions);
              return null;
            }
            const aspectRatio = dimensions.height / dimensions.width;
            const scaledHeight = MAX_IMAGE_WIDTH * aspectRatio;
            return {
              data: arrayBuffer,
              height: scaledHeight,
              id: item.id,
              type: imageType,
              width: MAX_IMAGE_WIDTH,
            };
          } catch (error) {
            console.warn('Failed to extract image dimensions, skipping image:', error);
            return null;
          }
        } catch (error) {
          console.error('Failed to fetch image:', item.image.cdnUrl, error);
          return null;
        }
      })
    );
    allResults.push(...batchResults);
  }

  return allResults.reduce((map, result) => {
    if (result) {
      map[result.id] = result;
    }
    return map;
  }, {});
};

/**
 * Creates a DOCX document from survey data with formatted questions, answers, and images
 * @param {Object} survey - Survey object containing questions, groups, and title
 * @param {String} userEmail - Email address of the user creating the document
 * @returns {Promise<Document>} Promise resolving to a docx Document object
 */
export const createDoc = async (survey, userEmail) => {
  const title = survey.title || 'Untitled Survey';
  const date = new Date().toLocaleDateString();
  const createdByText = userEmail
    ? `Verified Voices Questionnaire (ID: ${survey.id}) created by ${userEmail} on ${date}`
    : `Verified Voices Questionnaire (ID: ${survey.id}) created on ${date}`;

  const response = await fetch(NUMERATOR_FULL_LOGO_URL);
  if (!response.ok) {
    const msg = 'Failed to fetch logo image';
    console.error(msg, {
      status: response.status,
      statusText: response.statusText,
      url: NUMERATOR_FULL_LOGO_URL,
    });
    throw new Error(msg);
  }
  const logo = await response.arrayBuffer();

  // Exclude soft-deleted questions from the export
  const activeQuestions = (survey.questions || []).filter((q) => !q._delete);

  // Fetch all images in parallel (questions + answers)
  const allAnswers = activeQuestions.flatMap((q) => q.answers || []);
  const [questionImages, answerImages] = await Promise.all([
    prefetchImages(activeQuestions),
    prefetchImages(allAnswers),
  ]);

  const questionLogic = survey.questionLogic || {};
  const questions = [];
  const questionDisplayIndexById = getQuestionDisplayIndexById(activeQuestions);

  const createImage = (imageData, imageType, width, height, indent = INDENT_LG) =>
    new Paragraph({
      children: [
        new ImageRun({
          data: imageData,
          transformation: {
            height,
            width,
          },
          type: imageType,
        }),
      ],
      indent: { left: indent },
      spacing: DEFAULT_SPACING,
    });

  const getQuestionImageParagraph = (questionId) => {
    const image = questionImages[questionId];
    return image ? [createImage(image.data, image.type, image.width, image.height, INDENT_MD)] : [];
  };

  const createQuestionTitle = (question) =>
    new Paragraph({
      children: [
        new TextRun({
          ...BOLD_TEXT_CONFIG,
          text: `${questionDisplayIndexById[question.id]}. ${question.text}`,
        }),
      ],
      indent: { left: convertInchesToTwip(0.25) },
      spacing: QUESTION_SPACING,
    });

  const createSectionHeader = (text) =>
    new Paragraph({
      children: [new TextRun({ ...BASE_TEXT_CONFIG, size: SMALL_TEXT_SIZE, text })],
      indent: { left: INDENT_SM },
      spacing: HEADER_SPACING,
    });

  const getQuestionPropertiesText = (question) => {
    const properties = question?.properties;
    const propertyTexts = [];

    if (question?.showConceptImage) {
      propertyTexts.push('Concept Image Shown');
    }

    if (properties?.maxAnswers) {
      propertyTexts.push(`Max Answers: ${properties.maxAnswers}`);
    }

    if (properties?.randomizeOptions) {
      propertyTexts.push('Randomized Order');
    }

    if (properties?.randomizeSubQuestions) {
      propertyTexts.push('Randomized Question Rows');
    }

    return propertyTexts.length > 0 ? ' ' + propertyTexts.map((text) => `(${text})`).join(' ') : '';
  };

  const createQuestionMetadata = (typeName, question) => {
    const propertiesText = getQuestionPropertiesText(question);
    return new Paragraph({
      children: [new TextRun({ ...OPTION_TEXT_CONFIG, text: `(${typeName})${propertiesText}` })],
      indent: { left: INDENT_SM },
      spacing: DEFAULT_SPACING,
    });
  };

  const getAnswerAttributesText = (answer) => {
    const attributes = [];

    if (answer.isAnchored) {
      attributes.push('[PINNED]');
    }

    if (answer.isExclusive) {
      attributes.push('[EXCLUSIVE]');
    }

    return attributes.length > 0 ? ' ' + attributes.join(' ') : '';
  };

  const createAnswerParagraph = (symbol, answer) => {
    const attributesText = getAnswerAttributesText(answer);
    return new Paragraph({
      children: [
        new TextRun({
          ...BASE_TEXT_CONFIG,
          text: `${symbol}${EM_SPACE}${answer.text}${attributesText}`,
        }),
      ],
      indent: { left: INDENT_LG },
      spacing: { after: convertInchesToTwip(0.05) },
    });
  };

  const createAnswerParagraphs = (answers, symbol, imagesMap = {}) =>
    (answers || []).flatMap((answer) => {
      const paragraphs = [createAnswerParagraph(symbol, answer)];
      const image = imagesMap[answer.id];
      if (image) {
        paragraphs.push(createImage(image.data, image.type, image.width, image.height));
      }
      return paragraphs;
    });

  // Helper to create plain descriptive text
  const createDescriptiveText = (text, indent = INDENT_SM, size = SMALL_TEXT_SIZE) =>
    new Paragraph({
      children: [
        new TextRun({
          ...BASE_TEXT_CONFIG,
          italics: true,
          size,
          text,
        }),
      ],
      indent: { left: indent },
      spacing: DEFAULT_SPACING,
    });

  const getDisqualifyParagraphs = (disqualifyLogic) => {
    if (!disqualifyLogic?.answers?.length) return [];

    const paragraphs = [
      createDescriptiveText(
        `Disqualify if the answer is [${disqualifyLogic.operator?.text || ''}]:`
      ),
    ];

    disqualifyLogic.answers.forEach((ans) =>
      paragraphs.push(createDescriptiveText(`${EM_SPACE}${BULLET_SYMBOL} ${ans.text}`))
    );

    return paragraphs;
  };

  const getSkipLogicParagraphs = (skipLogic, questionType, groups) => {
    const conditions = skipLogic?.logic || [];
    if (conditions.length === 0) return [];

    const skipOperator = skipLogic.operator?.text || 'All';
    const isInstructional = questionType === QUESTION_TYPES.INSTRUCTIONS.value;
    const skipText = isInstructional ? 'Show this text' : 'Ask this question';

    const paragraphs = [createDescriptiveText(`${skipText} if [${skipOperator}] apply:`)];

    conditions.forEach((condition) => {
      if (condition.question) {
        // Question-based skip logic
        const questionNum = questionDisplayIndexById[condition.question?.id] || '?';
        const operator = condition.operator?.text || '';

        // Regular answer text (bulleted options list)
        if (condition.answers?.length) {
          const answerTexts = condition.answers.map((ans) => `${DASH_SYMBOL} ${ans.text}`);
          paragraphs.push(
            createDescriptiveText(
              `${EM_SPACE}${BULLET_SYMBOL} The answer to (Q${questionNum}) is [${operator}]:`
            )
          );
          answerTexts.forEach((answerText) =>
            paragraphs.push(createDescriptiveText(`${answerText}`, INDENT_LG))
          );
          // Numeric comparison
        } else {
          paragraphs.push(
            createDescriptiveText(
              `${EM_SPACE}${BULLET_SYMBOL} The answer to (Q${questionNum}) is [${operator}]: ${condition.comparison_value || ''}`
            )
          );
        }
      } else if (condition.url_variable || condition.values) {
        // Quota group skip logic
        const operator = condition.operator?.text || '';
        const groupNames = condition.values?.length
          ? condition.values
              .map((id) => groups?.find((g) => Number(g.id) === Number(id))?.name)
              .filter(Boolean)
              .join(', ')
          : '';
        paragraphs.push(
          createDescriptiveText(
            `${EM_SPACE}${BULLET_SYMBOL} The quota group is [${operator}]: ${groupNames}`
          )
        );
      }
    });

    return paragraphs;
  };

  const getQuestionLogicParagraphs = (question, groups) => {
    const logic = questionLogic[question.id];
    if (!logic) return [];

    const logicParagraphs = [
      ...getDisqualifyParagraphs(logic.disqualify),
      ...getSkipLogicParagraphs(logic.skip, question.type, groups),
    ];

    if (logicParagraphs.length === 0) return [];

    return [createSectionHeader('[LOGIC]'), ...logicParagraphs];
  };

  const createGridTableCell = (text, textConfig = BASE_TEXT_CONFIG, align = VerticalAlign.TOP) => {
    return new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ ...textConfig, text })],
          spacing: DEFAULT_SPACING,
        }),
      ],
      verticalAlign: align,
      width: { size: 50, type: WidthType.PERCENTAGE },
    });
  };

  const createGridTable = (question) => {
    const maxRows = Math.max((question.subQuestions || []).length, (question.answers || []).length);

    const rows = [
      // Header row
      new TableRow({
        children: [
          createGridTableCell('Grid Rows', BOLD_TEXT_CONFIG, VerticalAlign.CENTER),
          createGridTableCell('Grid Columns', BOLD_TEXT_CONFIG, VerticalAlign.CENTER),
        ],
      }),
      // Content rows
      ...Array.from({ length: maxRows }, (_, idx) => {
        const subQuestion = question.subQuestions?.[idx];
        const answer = question.answers?.[idx];
        const rowText = subQuestion ? `${idx + 1}. ${subQuestion.text}` : '';
        const colText = answer ? `${String.fromCharCode(A_CHAR_CODE + idx)}. ${answer.text}` : '';

        return new TableRow({
          children: [createGridTableCell(rowText), createGridTableCell(colText)],
        });
      }),
    ];

    return new Table({
      borders: BORDERLESS_TABLE_CONFIG.borders,
      indent: { size: INDENT_MD, type: WidthType.DXA },
      rows,
      width: { size: 90, type: WidthType.PERCENTAGE },
    });
  };

  const createStandardQuestion = (question, typeName, symbol = null, descriptiveText = null) => [
    createQuestionTitle(question),
    createQuestionMetadata(typeName, question),
    ...getQuestionImageParagraph(question.id),
    ...(descriptiveText ? [createDescriptiveText(descriptiveText)] : []),
    ...(symbol ? createAnswerParagraphs(question.answers, symbol, answerImages) : []),
    ...getQuestionLogicParagraphs(question, survey.groups),
  ];

  activeQuestions.forEach((question) => {
    switch (question.type) {
      case QUESTION_TYPES.CHECKBOX.value:
        questions.push(
          ...createStandardQuestion(question, QUESTION_TYPES.CHECKBOX.name, CHECKBOX_SYMBOL)
        );
        break;
      case QUESTION_TYPES.CONT_SUM.value:
      case QUESTION_TYPES.CONTSUM.value:
        questions.push(
          ...createStandardQuestion(question, QUESTION_TYPES.CONTSUM.name, FILLED_CIRCLE_SYMBOL)
        );
        break;
      case QUESTION_TYPES.ESSAY.value:
        questions.push(
          ...createStandardQuestion(
            question,
            QUESTION_TYPES.ESSAY.name,
            null,
            "Panelist's write-in"
          )
        );
        break;
      case QUESTION_TYPES.INSTRUCTIONS.value:
        questions.push(
          ...createStandardQuestion(
            question,
            QUESTION_TYPES.INSTRUCTIONS.name,
            null,
            'This instructional text does not count as a question'
          )
        );
        break;
      case QUESTION_TYPES.MAX_DIFF.value:
        const maxDiffInfo = getMaxDiffQuestionInfo(question);
        questions.push(
          ...createStandardQuestion(
            question,
            QUESTION_TYPES.MAX_DIFF.name,
            FILLED_CIRCLE_SYMBOL,
            maxDiffInfo.getText()
          )
        );
        break;
      case QUESTION_TYPES.NUMERIC.value:
        questions.push(
          ...createStandardQuestion(
            question,
            QUESTION_TYPES.NUMERIC.name,
            null,
            `Panelist's ${getNumericTypeText(question.properties)}`
          )
        );
        break;
      case QUESTION_TYPES.RADIO.value:
        questions.push(
          ...createStandardQuestion(question, QUESTION_TYPES.RADIO.name, RADIO_SYMBOL)
        );
        break;
      case QUESTION_TYPES.RANK.value:
      case QUESTION_TYPES['RANK-DRAGDROP'].value:
        questions.push(
          ...createStandardQuestion(question, QUESTION_TYPES.RANK.name, FILLED_CIRCLE_SYMBOL)
        );
        break;
      case QUESTION_TYPES.TABLE.value:
      case QUESTION_TYPES['TABLE-RADIO'].value:
        const questionCost = getQuestionCost(question);

        questions.push(
          createQuestionTitle(question),
          createQuestionMetadata(QUESTION_TYPES.TABLE.name, question),
          ...getQuestionImageParagraph(question.id),
          createDescriptiveText(
            `This will count as ${questionCost} question${questionCost !== 1 ? 's' : ''} for your survey.`
          ),
          createGridTable(question),
          ...getQuestionLogicParagraphs(question, survey.groups)
        );
        break;
      case QUESTION_TYPES.TURF.value:
        questions.push(
          ...createStandardQuestion(question, QUESTION_TYPES.TURF.name, CHECKBOX_SYMBOL)
        );
        break;
      default:
        questions.push(
          createQuestionTitle(question),
          ...getQuestionImageParagraph(question.id),
          ...getQuestionLogicParagraphs(question, survey.groups)
        );
        break;
    }
  });

  return new Document({
    numbering: {
      config: [BULLETED_LIST_CONFIG],
    },
    sections: [
      {
        children: [
          // HEADER
          new Paragraph({
            children: [
              new ImageRun({
                altText: {
                  description: 'Numerator Full Logo',
                  name: 'Numerator Full Logo',
                  title: 'Numerator Full Logo',
                },
                data: logo,
                transformation: {
                  height: 27,
                  width: 157,
                },
                type: 'png',
              }),
            ],
            spacing: HEADER_SPACING,
          }),
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, size: 28, text: title })],
            spacing: HEADER_SPACING,
          }),
          new Paragraph({
            children: [
              new TextRun({
                ...BASE_TEXT_CONFIG,
                text: createdByText,
              }),
            ],
            spacing: DEFAULT_SPACING,
          }),
          new Paragraph({
            children: [new TextRun({ ...BASE_TEXT_CONFIG, text: 'Powered by Verified Voices' })],
          }),
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, text: 'Response Counts:' })],
            spacing: BLOCK_SPACING,
          }),
          ...(survey.groups || []).map(
            (group) =>
              new Paragraph({
                children: [
                  new TextRun({
                    ...BOLD_TEXT_CONFIG,
                    text: `${group.name || ''}: ${group.quotaLimit || 0}`,
                  }),
                ],
                numbering: { level: 0, reference: 'bulleted-list' },
                spacing: DEFAULT_SPACING,
              })
          ),
          // QUESTIONNAIRE
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, text: 'Questionnaire:' })],
            spacing: BLOCK_SPACING,
          }),
          ...questions,
        ],
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.END,
                children: [
                  new TextRun({
                    children: [PageNumber.CURRENT],
                  }),
                ],
              }),
            ],
          }),
        },
        properties: {
          page: {
            pageNumbers: {
              formatType: NumberFormat.DECIMAL,
              start: 1,
            },
          },
        },
      },
    ],
  });
};

/**
 * Generates and downloads a DOCX file from a Document object
 * @param {Document} doc - docx Document object to convert and download
 * @param {String} fileName - Name for the downloaded file (including .docx extension)
 * @returns {Promise<void>}
 */
export const generateFile = async (doc, fileName) => {
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);

  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
